import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IconPencil,
  IconCheck,
  IconX,
  IconTruckDelivery,
  IconPackage,
  IconClock,
  IconCreditCard,
  IconUser,
  IconShoppingCart,
  IconInfoCircle
} from "@tabler/icons-react";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { fetchOrderDetails, updateOrderStatusAdmin } from "../../redux/slices/ordersSlice";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import ErrorFallback from "../../components/common/ErrorFallback";
import LoadingFallback from "../../components/common/LoadingFallback";

const OrderDetail = () => {
  const { orderUid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  // Redux state
  const { currentOrder, loading, error } = useSelector((state) => state.orders);

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [availableStatuses, setAvailableStatuses] = useState([]);

  // Order status hierarchy
  const ORDER_STATUS = {
    ORDERING: 'Ordering',
    ORDER_PLACED: 'Order Placed',
    PENDING: 'Pending',
    SHIPPED: 'Shipped',
    OUT_FOR_DELIVERY: 'Out for Delivery',
    DELIVERED: 'Delivered',
    CANCELLED: 'Cancelled',
    REFUNDED: 'Refunded'
  };

  const STATUS_HIERARCHY = [
    ORDER_STATUS.ORDERING,
    ORDER_STATUS.ORDER_PLACED,
    ORDER_STATUS.PENDING,
    ORDER_STATUS.SHIPPED,
    ORDER_STATUS.OUT_FOR_DELIVERY,
    ORDER_STATUS.DELIVERED,
    ORDER_STATUS.CANCELLED,
    ORDER_STATUS.REFUNDED
  ];

  // Fetch order details on mount
  useEffect(() => {
    setLayoutConfig({
      title: "Order Management / Edit",
      showBackButton: true,
      scrollable: true,
    });
    dispatch(fetchOrderDetails(orderUid));
  }, [dispatch, orderUid, setLayoutConfig]);

  // Set available statuses based on current status and payment status
  useEffect(() => {
    if (currentOrder) {
      const currentStatusIndex = STATUS_HIERARCHY.indexOf(currentOrder.status);
      let possibleStatuses = [];

      // If payment is not completed, limit status progression
      if (currentOrder.payment_detail?.status !== 'COMPLETED') {
        possibleStatuses = STATUS_HIERARCHY.slice(0, STATUS_HIERARCHY.indexOf(ORDER_STATUS.SHIPPED) + 1);
      } else {
        // Allow all statuses higher than current
        possibleStatuses = STATUS_HIERARCHY.slice(currentStatusIndex + 1);
      }

      // Always allow cancellation
      if (!possibleStatuses.includes(ORDER_STATUS.CANCELLED)) {
        possibleStatuses.push(ORDER_STATUS.CANCELLED);
      }

      setAvailableStatuses(possibleStatuses);
      setSelectedStatus(currentOrder.status);
    }
  }, [currentOrder]);

  const handleStatusUpdate = async () => {
    try {
      await dispatch(updateOrderStatusAdmin({
        orderUid,
        newStatus: selectedStatus
      })).unwrap();
      setShowStatusModal(false);
      setIsEditing(false);
      dispatch(fetchOrderDetails(orderUid));
    } catch (error) {
      console.error('Failed to update order status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case ORDER_STATUS.DELIVERED:
        return "bg-green-100 text-green-800";
      case ORDER_STATUS.CANCELLED:
      case ORDER_STATUS.REFUNDED:
        return "bg-red-100 text-red-800";
      case ORDER_STATUS.SHIPPED:
      case ORDER_STATUS.OUT_FOR_DELIVERY:
        return "bg-blue-100 text-blue-800";
      case ORDER_STATUS.PENDING:
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return "bg-green-100 text-green-800";
      case 'PENDING':
        return "bg-yellow-100 text-yellow-800";
      case 'FAILED':
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) return <LoadingFallback />;
  if (error) return <ErrorFallback error={error} onRetry={() => dispatch(fetchOrderDetails(orderUid))} />;

  return (
    // <AdminLayout title={"Order Management / Edit"} scrollable={true} showBackButton={true}>
    <div>
      <div className="flex-1 flex flex-col">
        <main className="p-6 flex-1 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Order Summary */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                  <IconShoppingCart size={20} />
                  Order Summary
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Order UID</p>
                    <p className="font-medium">{currentOrder?.order_uid}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-medium">
                      {new Date(currentOrder?.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className={`px-2 py-1 rounded inline-block ${getStatusColor(currentOrder?.status)}`}>
                      {currentOrder?.status}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Status</p>
                    <div className={`px-2 py-1 rounded inline-block ${getPaymentStatusColor(currentOrder?.payment_detail?.status)}`}>
                      {currentOrder?.payment_detail?.status}
                    </div>
                  </div>
                </div>

                {isEditing && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Update Status</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value={currentOrder?.status}>Current: {currentOrder?.status}</option>
                      {availableStatuses.map(status => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                    {selectedStatus !== currentOrder?.status && (
                      <p className="text-xs text-gray-500 mt-1">
                        Changing status from {currentOrder?.status} to {selectedStatus}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Customer Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                  <IconUser size={20} />
                  Customer Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{currentOrder?.user_detail?.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{currentOrder?.user_detail?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{currentOrder?.user_detail?.phone_number}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
              <IconPackage size={20} />
              Order Items
            </h3>

            <div className="space-y-4">
              {currentOrder?.order_detail?.map((item, index) => (
                <div key={index} className="flex flex-col md:flex-row border rounded-lg p-4">
                  <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-4">
                    <img
                      src={item.product_image.front_side_image}
                      alt={item.product_name}
                      className="w-32 h-32 object-contain rounded border"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-lg">{item.product_name}</h4>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <p className="text-sm text-gray-500">Price</p>
                        <p className="font-medium">${item.product_price.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Quantity</p>
                        <p className="font-medium">{item.quantity}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Subtotal</p>
                        <p className="font-medium">${(item.product_price * item.quantity).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Customized</p>
                        <p className="font-medium">{item.is_customized_design ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment & Shipping */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
                <IconCreditCard size={20} />
                Payment Details
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <p className="text-gray-500">Subtotal</p>
                  <p className="font-medium">${currentOrder?.total_amount}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-gray-500">Shipping</p>
                  <p className="font-medium">${currentOrder?.shipping_fee}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-gray-500">Tax</p>
                  <p className="font-medium">${currentOrder?.tax_amount}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-gray-500">Discount</p>
                  <p className="font-medium">${currentOrder?.discount_amount}</p>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <p className="font-semibold">Total</p>
                  <p className="font-semibold">${currentOrder?.grand_total}</p>
                </div>

                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Payment Method</p>
                  <p>{currentOrder?.payment_detail?.method}</p>
                </div>

                {currentOrder?.payment_receipt_details && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium">Transaction ID</p>
                    <p>{currentOrder?.payment_receipt_details.id}</p>
                    <p className="text-sm font-medium">Amount Paid</p>
                    <p>{currentOrder?.payment_receipt_details.amount}</p>
                    <p className="text-sm font-medium">Status</p>
                    <p>{currentOrder?.payment_receipt_details.status}</p>
                    {currentOrder?.payment_receipt_details.receipt_url && (
                      <a
                        href={currentOrder?.payment_receipt_details.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        View Receipt
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
                <IconTruckDelivery size={20} />
                Shipping Details
              </h3>

              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Carrier</p>
                  <p>{currentOrder?.shipping_details?.carrier_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Shipping Type</p>
                  <p>{currentOrder?.shipping_details?.shipping_type || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Estimated Delivery</p>
                  <p>
                    {currentOrder?.shipping_details?.delivery_days
                      ? `${currentOrder.shipping_details.delivery_days} business days`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Shipping Cost</p>
                  <p>${currentOrder?.shipping_fee || '0.00'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Tracking Number</p>
                  <p>{currentOrder?.tracking_number || 'Not available yet'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
              <IconClock size={20} />
              Order Timeline
            </h3>

            <div className="relative pl-6 border-l-2 border-gray-200 space-y-4">
              <div className="relative">
                <div className="absolute -left-7 top-0 w-4 h-4 rounded-full bg-blue-500"></div>
                <div className="pl-4">
                  <p className="font-medium">Order Placed</p>
                  <p className="text-sm text-gray-500">
                    {new Date(currentOrder?.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {currentOrder?.updated_at && (
                <div className="relative">
                  <div className="absolute -left-7 top-0 w-4 h-4 rounded-full bg-green-500"></div>
                  <div className="pl-4">
                    <p className="font-medium">Last Updated</p>
                    <p className="text-sm text-gray-500">
                      {new Date(currentOrder?.updated_at).toLocaleString()}
                    </p>
                    <p className="text-sm">Status: {currentOrder?.status}</p>
                  </div>
                </div>
              )}
            </div>
          </div>



          <div className="flex justify-center items-center mt-6">
            <div className="flex items-center gap-4">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded text-sm"
                >
                  <IconPencil size={16} />
                  Update Status
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-3 py-1 bg-gray-300 rounded text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowStatusModal(true)}
                    className="px-3 py-1 bg-green-500 text-white rounded text-sm flex items-center gap-1"
                  >
                    <IconCheck size={16} />
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirm Status Update</h3>
              <button onClick={() => setShowStatusModal(false)}>
                <IconX size={20} />
              </button>
            </div>
            <p className="mb-4">
              Are you sure you want to update the order status from <strong>{currentOrder?.status}</strong> to <strong>{selectedStatus}</strong>?
            </p>
            {currentOrder?.payment_detail?.status !== 'COMPLETED' &&
              selectedStatus !== ORDER_STATUS.CANCELLED &&
              STATUS_HIERARCHY.indexOf(selectedStatus) > STATUS_HIERARCHY.indexOf(ORDER_STATUS.PENDING) && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded mb-4">
                  <IconInfoCircle size={18} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-yellow-700">
                    Payment is not completed. This status change might not be appropriate.
                  </p>
                </div>
              )}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                Confirm Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;